// Builds the merged, queryable map model from raw building + nav JSON files.
//
// Expected file formats (what the UMaps Building Creator / Node Map Editor export):
//   <building>.json      = { building, polygons: [{floor, type, coordinates: [{latitude, longitude}], room_number}] }
//   nav_<building>.json  = { building, nodes:  [{id, lat, lng, floor, type, building, room_number?, label?}],
//                            edges: [{id, from, to, weight, outdoor, tunnel}],
//                            room_doors: [{room_number, node_id}] }
//
// Floor 0 = outdoor. Cross-building edges are exported into BOTH buildings'
// nav files, so nodes and edges are deduplicated by id here.

import { campusDirection } from './transit.js';
import { isBikeLabel } from './bikes.js';
import { lotDisplayName, lotFullName, lotRate } from './parking.js';

export const SHAFT_TYPES = new Set(['stair', 'elevator']);

/**
 * The whole stair or elevator shaft `nodeId` belongs to: every same-type node
 * reached by walking cross-floor edges whose BOTH ends share that type (the
 * floor-to-floor links of the shaft-link system — the same edges route.js's
 * `transitionKind` calls a floor switch), the ids of those edges, and the
 * floors they join, ascending. Same-floor edges touching the shaft (the
 * corridor onto a landing) are not part of it, so refusing a shaft's
 * `edgeIds` stops the ride and leaves every landing walkable — which is what
 * the Route tester's "Disable shaft" edit feeds findRoute's `excludeEdges`.
 * `nodeIds` starts with `nodeId`. Returns null for any other node type, or an
 * unknown id.
 * @returns {{ type: 'stair'|'elevator', nodeIds: string[], edgeIds: string[], floors: number[] } | null}
 */
export function shaftOf(mapData, nodeId) {
  const nodes = mapData?.nodes, adjacency = mapData?.adjacency;
  const start = nodes?.get(nodeId);
  if (!start || !SHAFT_TYPES.has(start.type)) return null;
  const type = start.type;
  const nodeIds = [nodeId];
  const seen = new Set(nodeIds);
  const edgeIds = [];
  const edgeSeen = new Set();
  const floors = new Set([start.floor]);
  for (let i = 0; i < nodeIds.length; i++) {
    const here = nodes.get(nodeIds[i]);
    for (const { edge, other } of adjacency?.get(nodeIds[i]) || []) {
      const n = nodes.get(other);
      if (!n || n.type !== type || n.floor === here.floor) continue; // only the shaft's own floor links
      if (!edgeSeen.has(edge.id)) { edgeSeen.add(edge.id); edgeIds.push(edge.id); }
      if (seen.has(other)) continue;
      seen.add(other);
      nodeIds.push(other);
      floors.add(n.floor);
    }
  }
  return { type, nodeIds, edgeIds, floors: [...floors].sort((a, b) => a - b) };
}

/** "105st" → 'stair', "128el" / "100Ael" → 'elevator', anything else → null. */
function shaftTypeOf(roomNumber) {
  const m = String(roomNumber || '').trim().match(/\d{2}[a-z]?(st|el)$/i);
  return m ? (m[1].toLowerCase() === 'el' ? 'elevator' : 'stair') : null;
}

/**
 * @param {Array<{slug: string, name?: string, building?: object, nav?: object}>} files
 * @returns MapData: {
 *   nodes: Map<id, node>, edges: Map<id, edge>,
 *   adjacency: Map<nodeId, Array<{edge, other}>>,
 *   roomIndex: [{building, buildingName, roomNumber, nodeId}],
 *   buildings: [{slug, name, floors: number[], polygonsByFloor: Map<floor, polygons[]>, outlines: ring[]}],
 *   warnings: string[]
 * }
 */
export function buildMapData(files) {
  const warnings = [];
  const nodes = new Map();
  const edges = new Map();
  const roomIndex = [];
  const buildings = [];
  const buildingNames = new Map(); // slug → display name
  // One search entry per room, grouping every door node of that room together
  // so a room with several entrances (possibly on different floors) is listed
  // ONCE and can be routed to via whichever door is nearest.
  const roomGroups = new Map(); // `${slug}::${roomNumber.toLowerCase()}` → entry

  for (const f of files) {
    const slug = f.slug || f.nav?.building || f.building?.building || 'unknown';
    const name = f.name || prettifySlug(slug);
    buildingNames.set(slug, name);

    // ── Structure polygons ────────────────────────────────────────────────
    if (f.building?.polygons) {
      const polygonsByFloor = new Map();
      const outlines = [];
      const floorSet = new Set();
      for (const poly of f.building.polygons) {
        const ring = (poly.coordinates || []).map(c => ({ lat: c.latitude, lng: c.longitude }));
        if (ring.length < 3) continue;
        const entry = { floor: poly.floor, type: poly.type, roomNumber: poly.room_number || '', ring };
        if (!polygonsByFloor.has(poly.floor)) polygonsByFloor.set(poly.floor, []);
        polygonsByFloor.get(poly.floor).push(entry);
        floorSet.add(poly.floor);
        if (poly.type === 'outline') outlines.push(ring);
      }
      buildings.push({
        slug, name,
        // A footprint-only stub still advertises the floors it HAS, from the
        // bundle, so the floor switcher and route rendering don't have to wait
        // on an interior fetch that may never happen.
        floors: f.building.floors?.length
          ? [...f.building.floors].sort((a, b) => a - b)
          : [...floorSet].sort((a, b) => a - b),
        polygonsByFloor, outlines,
        // Interiors deferred — see attachBuildingInterior().
        outlineOnly: !!f.building.outlineOnly,
      });
    } else {
      buildings.push({ slug, name, floors: [], polygonsByFloor: new Map(), outlines: [], outlineOnly: false });
    }

    // ── Nav nodes ─────────────────────────────────────────────────────────
    if (!f.nav) continue;
    for (const n of f.nav.nodes || []) {
      if (nodes.has(n.id)) continue; // same node exported by a partner building's file
      // Shaft-numbered nodes ("105st"/"128el") must be stair/elevator typed for
      // floor-switch detection — older exports carry them as plain doors.
      let type = n.type || 'waypoint';
      const shaft = shaftTypeOf(n.room_number);
      if (shaft && (type === 'door' || type === 'waypoint')) type = shaft;
      nodes.set(n.id, { ...n, building: n.building || slug, type });
    }
  }

  // ── Edges (second pass so cross-building endpoints from any file exist) ──
  for (const f of files) {
    for (const e of f.nav?.edges || []) {
      if (edges.has(e.id)) continue;
      if (!nodes.has(e.from) || !nodes.has(e.to)) {
        warnings.push(`Edge ${e.id} dropped — missing endpoint (${e.from} → ${e.to}). Is a partner building's nav file missing from the manifest?`);
        continue;
      }
      edges.set(e.id, { ...e, weight: e.weight || 1, outdoor: !!e.outdoor, tunnel: !!e.tunnel });
    }
    // ── Room search index (grouped by room, all door nodes collected) ─────
    const slug = f.slug || f.nav?.building || 'unknown';
    for (const d of f.nav?.room_doors || []) {
      if (!d.room_number || !nodes.has(d.node_id)) continue;
      const key = `${slug}::${String(d.room_number).toLowerCase()}`;
      let entry = roomGroups.get(key);
      if (!entry) {
        entry = {
          building: slug,
          buildingName: buildingNames.get(slug) || slug,
          roomNumber: String(d.room_number),
          nodeId: d.node_id,   // representative door (first seen)
          nodeIds: [],         // every door of this room — routing picks the nearest
        };
        roomGroups.set(key, entry);
        roomIndex.push(entry);
      }
      if (!entry.nodeIds.includes(d.node_id)) entry.nodeIds.push(d.node_id);
    }
  }

  // ── Parking lots as searchable destinations ─────────────────────────────
  // Built from the nodes alone here, which is all this function has. Once the
  // authored lots file has been loaded, `attachParking` replaces these with the
  // real named areas — see `parkingEntries`.
  for (const entry of parkingEntries(nodes, null)) roomIndex.push(entry);

  // ── Adjacency ─────────────────────────────────────────────────────────────
  const adjacency = new Map();
  for (const id of nodes.keys()) adjacency.set(id, []);
  for (const edge of edges.values()) {
    adjacency.get(edge.from).push({ edge, other: edge.to });
    adjacency.get(edge.to).push({ edge, other: edge.from });
  }

  // ── Reachability flag on search entries ────────────────────────────────────
  // A "lonely" room is one whose every door node has no edges — it can't be a
  // route endpoint (there's no path to or from it). Flag each entry so the UI
  // can grey it out. This is derived, not stored: adjacency is already built,
  // so it costs one linear pass and is recomputed on every (re)load — dropped-in
  // files stay live without touching the data.
  for (const entry of roomIndex) {
    entry.connected = entry.nodeIds.some(id => (adjacency.get(id)?.length || 0) > 0);
  }

  // ── Validation warnings ───────────────────────────────────────────────────
  // A building with several floors but no cross-floor edge is un-routable
  // between its own floors — the #1 "no route found" cause. (Fix: Node Map
  // Editor → load the building → Actions → Shaft Links → ⚡ Link all → Save.)
  {
    const floorsSeen = new Map(); // building → Set<floor>
    const crossFloorBuildings = new Set();
    for (const n of nodes.values()) {
      if (!floorsSeen.has(n.building)) floorsSeen.set(n.building, new Set());
      if (n.floor !== 0) floorsSeen.get(n.building).add(n.floor);
    }
    for (const e of edges.values()) {
      const a = nodes.get(e.from), b = nodes.get(e.to);
      if (a.building === b.building && a.floor !== b.floor) crossFloorBuildings.add(a.building);
    }
    for (const [bldg, floors] of floorsSeen) {
      if (floors.size > 1 && !crossFloorBuildings.has(bldg))
        warnings.push(`${bldg} spans floors ${[...floors].sort((x, y) => x - y).join(', ')} but has NO cross-floor edges — its stairs/elevators aren't linked (Node Map Editor → Actions → Shaft Links → Link all).`);
    }
  }
  for (const n of nodes.values()) {
    const neighbors = adjacency.get(n.id);
    if (neighbors.length === 0) {
      warnings.push(`Node ${n.id} (${n.type}, ${n.building} F${n.floor}) has no edges — unreachable.`);
      continue;
    }
    // A shaft node should be walkable-to on its own floor, not just linked vertically.
    if (SHAFT_TYPES.has(n.type) && n.room_number) {
      const sameFloor = neighbors.some(({ other }) => nodes.get(other)?.floor === n.floor);
      if (!sameFloor) warnings.push(`Shaft node ${n.room_number} (${n.building} F${n.floor}) has no same-floor edge — it can't be reached from its own floor.`);
    }
  }

  return { nodes, edges, adjacency, roomIndex, buildings, warnings };
}

function prettifySlug(slug) {
  return String(slug).replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Parking lots ─────────────────────────────────────────────────────────────
// Flattens parking_lots.json into drawable rings, tolerant of shape variations
// so a freshly dropped-in file just works: accepts a top-level array or
// `{lots:[…]}`, `latitude/longitude` or `lat/lng`, and skips empty polygons.
/**
 * Fold a building's full polygon file into a footprint-only entry, in place.
 *
 * loadAllMapFiles ships outlines and nothing else, so a building carries
 * `outlineOnly: true` until the user does something that needs its insides —
 * routes through it, selects a room in it, previews a place in it. This
 * replaces the stub's polygons with the real set and clears the flag.
 *
 * Mutates rather than rebuilding because mapData is shared by reference all
 * over the viewer (nodes, adjacency, roomIndex); re-running buildMapData for
 * one building would throw away and re-derive the whole graph. Callers signal
 * the change with a counter so React re-renders what draws from it.
 *
 * @returns true when something was actually merged
 */
export function attachBuildingInterior(mapData, slug, buildingJson) {
  const b = mapData?.buildings?.find(x => x.slug === slug);
  if (!b || !buildingJson?.polygons) return false;
  const polygonsByFloor = new Map();
  const outlines = [];
  const floorSet = new Set();
  for (const poly of buildingJson.polygons) {
    const ring = (poly.coordinates || []).map(c => ({ lat: c.latitude, lng: c.longitude }));
    if (ring.length < 3) continue;
    if (!polygonsByFloor.has(poly.floor)) polygonsByFloor.set(poly.floor, []);
    polygonsByFloor.get(poly.floor).push({
      floor: poly.floor, type: poly.type, roomNumber: poly.room_number || '', ring,
    });
    floorSet.add(poly.floor);
    if (poly.type === 'outline') outlines.push(ring);
  }
  if (!polygonsByFloor.size) return false;
  b.polygonsByFloor = polygonsByFloor;
  b.outlines = outlines;
  b.floors = [...floorSet].sort((a, x) => a - x);
  b.outlineOnly = false;
  return true;
}

/**
 * The outdoor parking nodes, grouped into things somebody can search for.
 *
 * A node typed `parking` is an ENTRANCE — it sits on the path at the mouth of
 * the lot, not inside it (checked: not one of the 23 falls within any lot
 * polygon), and it carries the lot's key in `label`: "Q", "ACW", "Dafoe Bike
 * Lockers". Nodes sharing a label are entrances to the same place, so they
 * group into one destination carrying every one of them and routing picks the
 * nearest, exactly as a room with several doors does.
 *
 * WHAT THE LOTS FILE CHANGES. Without it all this function can say is the
 * label — and "Parking Lot Q" is not what anything on this campus is called.
 * With it, a lot's key resolves to the AREAS it is actually divided into, each
 * with the name printed on its own sign: Q is four of them ("Q Lot – Carpool",
 * "– Oversized Vehicle", "– Student Permit", "– Paid/Visitor"). They are
 * separate results because they are separate answers — a student permit is no
 * use in the visitor half — and they share the lot's entrance nodes because
 * that is the truth: you drive in the same way whichever part you are heading
 * for. The same names the map labels print, through the same `lotFullName`, so
 * the map and the search box cannot disagree.
 *
 * BIKE PARKING IS ITS OWN KIND. The file keeps bike racks in the same list as
 * the car lots (as empty shells, with no sections and no schedule), and they
 * are not parking lots in any sense a reader cares about — you do not drive to
 * one, and "Parking Lot Dafoe Bike Lockers" was wrong twice over. They keep
 * their own name and are flagged `bikeParking`, which is what the search row
 * tags and what the map's card is opened from. Routing is unchanged: the
 * assigned node is the destination, the way it always was.
 *
 * @param nodes        Map<id, node> — the whole graph's nodes
 * @param parkingJson  the authored lots→sections file, or null
 */
export function parkingEntries(nodes, parkingJson) {
  // label (lower) → { key, sections, drawn }, from the authored file.
  //
  // Section OBJECTS, not just their names: a row has to know what its area
  // costs, which is `lotRate(section)` over the section's own category and
  // hourly rate. `drawn` is whether any of them has a polygon — the thing that
  // decides whether a lot with no entrance node is worth listing at all.
  const authored = new Map();
  const lots = Array.isArray(parkingJson) ? parkingJson
    : (Array.isArray(parkingJson?.lots) ? parkingJson.lots : []);
  for (const lot of lots) {
    const key = String(lot?.lot ?? lot?.name ?? '').trim();
    if (!key) continue;
    const all = lot?.sections || [];
    const sections = all
      .filter(s => s && String(s.name || '').trim())
      .map(s => ({
        name: String(s.name).trim(),
        rate: lotRate(s),
        drawn: (s.polygon || []).length >= 3,
      }));
    authored.set(key.toLowerCase(), {
      key,
      sections,
      // ⚠️ Over EVERY section, including the nameless ones. `parseParkingPolygons`
      // draws any ring it finds and does not care whether the section was named,
      // so a lot can be painted on the map by a section this list drops. H is
      // exactly that: one named area with no polygon, plus an unnamed one
      // carrying the shape. Testing only the named sections declared H undrawn
      // and left the lot on screen with nothing to search for — the very bug
      // this block exists to fix.
      drawn: all.some(s => (s?.polygon || []).length >= 3),
    });
  }

  const groups = new Map();   // label (lower) → { label, building, nodeIds }
  for (const n of nodes.values()) {
    if (n.type !== 'parking') continue;
    const label = String(n.label || '').trim();
    if (!label) continue;                       // an unlabelled node is unsearchable
    const key = label.toLowerCase();
    let g = groups.get(key);
    if (!g) { g = { label, building: n.building || 'outdoors', nodeIds: [] }; groups.set(key, g); }
    if (!g.nodeIds.includes(n.id)) g.nodeIds.push(n.id);
  }

  const out = [];
  for (const [key, g] of groups) {
    const base = {
      building: g.building,
      roomNumber: '',
      nodeId: g.nodeIds[0],       // representative, as a room's first door is
      nodeIds: g.nodeIds,
      parking: true,
    };
    if (isBikeLabel(g.label)) {
      out.push({ ...base, buildingName: g.label, bikeParking: true, parkingKind: 'bike' });
      continue;
    }
    // Bus stops carry their whole name in the label and are not lots at all.
    if (/bus stop/i.test(g.label)) { out.push({ ...base, buildingName: g.label }); continue; }
    // A dropoff bay is in this list because it is an outdoor node the tool
    // types `parking`, but it is not somewhere you park — you stop, somebody
    // gets out, you leave. Tagging seven of these PARKING in the results would
    // be a small lie on every one of them, so they say what they are.
    const dropoff = /drop-?off/i.test(g.label);

    const lot = authored.get(key);
    const sections = lot?.sections || [];
    if (!sections.length) {
      // No file, or a lot with no areas listed (the dropoff bays). The label is
      // the best name there is; `lotDisplayName` turns a bare letter into "Q
      // Lot" and leaves anything that already names its kind alone.
      out.push({
        ...base,
        buildingName: lotDisplayName(g.label),
        parkingKind: dropoff ? 'dropoff' : 'lot',
        ...(dropoff ? {} : lotSearchFields(g.label)),
      });
      continue;
    }
    for (const s of sections) {
      out.push({
        ...base, buildingName: lotFullName(s.name), parkingSection: s.name,
        parkingKind: dropoff ? 'dropoff' : 'lot',
        paid: !!s.rate, rate: s.rate,
        ...(dropoff ? {} : lotSearchFields(lot.key)),
      });
    }
  }

  // ── Lots that are DRAWN but have no entrance node ────────────────────────
  //
  // A node typed `parking` is what makes a lot routable, and eight of the lots
  // the map paints have none: G, H, M, SD, ALC, L, St. John's, X. They used to
  // produce no search row at all, which is the worst of the three options — a
  // lot is sitting on the screen in its own colour with its price on it, and
  // typing its name finds nothing, which reads as the search being broken
  // rather than as the map knowing something the router does not.
  //
  // So they are listed with no nodes, which `attachParking` turns into
  // `connected: false` and the result row draws greyed out with "no route" —
  // the same treatment as a room whose doors were never linked, and the same
  // rule Course Mode follows for a section with no room: shown and disabled,
  // never filtered out. The honest fix is upstream, an entrance node in
  // UMapsTools; until then the reader learns the lot exists and can tap it on
  // the map for its hours and price.
  //
  // Only DRAWN lots. One that is in the file with neither a node nor a polygon
  // is invisible everywhere else, and listing it would add rows for a lot
  // nobody can see, point at, or drive to.
  for (const [key, lot] of authored) {
    if (groups.has(key) || !lot.drawn) continue;
    const base = {
      building: 'outdoors', roomNumber: '',
      nodeId: null, nodeIds: [],
      parking: true, parkingKind: 'lot',
      ...lotSearchFields(lot.key),
    };
    if (!lot.sections.length) {
      out.push({ ...base, buildingName: lotDisplayName(lot.key) });
      continue;
    }
    for (const s of lot.sections) {
      out.push({
        ...base, buildingName: lotFullName(s.name), parkingSection: s.name,
        paid: !!s.rate, rate: s.rate,
      });
    }
  }
  return out;
}

/**
 * What a lot answers to in the search box, beyond the name printed on its row.
 *
 * ⚠️ `searchKey` exists because most of this campus's lots are called a single
 * letter, and a single letter is a substring of nearly every other lot's name.
 * Typing "D lot" scored "Freedman Crescent Lot – Paid/Visitor" exactly as
 * highly as "D Lot – Paid/Visitor" — both contain a "d" and both contain "lot"
 * — so the lot somebody was actually asking for arrived below five others, or
 * off the end of the list entirely. An EXACT match on the lot key outranks
 * every substring, which is the only rule that can separate "D" from "Freedman".
 *
 * `searchAlso` is scored lowest of all, so "parking" lists the lots without
 * letting the word outrank anything named for it.
 */
function lotSearchFields(key) {
  return { searchKey: String(key || '').toLowerCase(), searchAlso: 'parking lot car' };
}

export function parseParkingPolygons(json) {
  const lots = Array.isArray(json) ? json : (Array.isArray(json?.lots) ? json.lots : []);
  const out = [];
  for (const lot of lots) {
    const lotName = String(lot?.lot ?? lot?.name ?? '').trim();
    for (const section of (lot?.sections || [])) {
      const ring = (section?.polygon || [])
        .map(c => ({ lat: Number(c?.latitude ?? c?.lat), lng: Number(c?.longitude ?? c?.lng) }))
        .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng));
      if (ring.length >= 3) out.push({ lot: lotName, section: String(section?.name || ''), ring });
    }
  }
  return out;
}

/**
 * Adds parking-lot polygons to mapData as a synthetic 'parking' building (floor
 * 0) so the map renders them, and re-names the parking search entries from the
 * same file — the lot keys the nodes carry ("Q") become the areas people
 * actually ask for ("Q Lot – Student Permit").
 *
 * Idempotent in both halves: it replaces any prior parking building, and drops
 * the previous parking rows from the room index before adding its own, the way
 * `attachTransitStops` does. Called twice, it must leave one of each.
 *
 * Returns { lots, polygons, entries }.
 */
export function attachParking(mapData, parkingJson) {
  if (Array.isArray(mapData?.roomIndex)) {
    const kept = mapData.roomIndex.filter(e => !e?.parking);
    for (const entry of parkingEntries(mapData.nodes, parkingJson)) {
      // Re-derived here, not inherited: `connected` is what greys out a result
      // that has no path to it, and it is computed in buildMapData — these rows
      // are made after that pass and would otherwise arrive undefined, quietly
      // promising a route to an entrance nobody drew an edge to.
      entry.connected = entry.nodeIds.some(id => (mapData.adjacency?.get(id)?.length || 0) > 0);
      kept.push(entry);
    }
    // Rewritten in place: callers (and `getRoomLocation`) hold this array.
    mapData.roomIndex.length = 0;
    mapData.roomIndex.push(...kept);
  }
  const polys = parseParkingPolygons(parkingJson);
  const byFloor = new Map();
  if (polys.length) {
    byFloor.set(0, polys.map(p => ({ floor: 0, type: 'parking', roomNumber: p.lot, ring: p.ring })));
  }
  const building = { slug: 'parking', name: 'Parking', floors: polys.length ? [0] : [], polygonsByFloor: byFloor, outlines: [] };
  const i = mapData.buildings.findIndex(b => b.slug === 'parking');
  if (i >= 0) mapData.buildings[i] = building; else mapData.buildings.push(building);
  return {
    lots: new Set(polys.map(p => p.lot)).size,
    polygons: polys.length,
    entries: (mapData?.roomIndex || []).filter(e => e?.parking).length,
  };
}

// ── Room info (capacity + type from the reference spreadsheet) ───────────────
// The spreadsheet's building names don't match app names exactly. We resolve
// each sheet name to an app building by a token-subset match tolerant of:
//   · extra words on either side  ("Allen" ↔ "Allen Physics",
//                                   "Russell" ↔ "John A Russell")
//   · apostrophes/possessives     ("St. John's College" ↔ "St Johns College")
//     — length-1 tokens are dropped and plural forms (john/johns) treated equal.
// Score rewards more matched tokens and penalizes leftover app tokens, so the
// tightest building wins ("Education" beats "Extended Education Complex").
function normNameTokens(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
    .split(/\s+/).filter(t => t.length > 1); // drop 1-char tokens ("a", stray "s")
}
function tokenEq(a, b) {
  return a === b || `${a}s` === b || `${b}s` === a; // plural-tolerant
}
function subsetMatchScore(sheetTokens, appTokens) {
  if (!sheetTokens.length || !appTokens.length) return 0;
  let matched = 0;
  for (const st of sheetTokens) {
    if (appTokens.some(at => tokenEq(st, at))) matched++;
    else return 0; // every sheet token must be present
  }
  // Require at least one substantive (≥4 char) shared token to avoid a lone
  // short token ("Art") latching onto an unrelated building.
  const strong = sheetTokens.some(st => st.length >= 4 && appTokens.some(at => tokenEq(st, at)));
  if (!strong) return 0;
  return matched * 100 - (appTokens.length - matched);
}

/**
 * Annotate mapData.roomIndex entries in place with `roomType`, `roomTypeDetail`
 * and `capacity` from the reference spreadsheet rows (see
 * scripts/convert-room-info.py for the row shape). Also stashes the lookup on
 * `mapData.roomInfo` (Map `"<slug>::<roomNumber>"` → row). Returns
 * `{ matched, total, unmatchedBuildings }` — useful for a data-coverage note.
 */
/**
 * Make the campus bus stops searchable, the same way parking lots are.
 *
 * A search result is something you pick and then get routed to, so only a stop
 * with a walk-from node earns an entry: the pole itself is not on the path
 * network, and a result that resolves to nowhere is worse than no result. Stops
 * without one are still listed in the Nearby panel, where "where is it and when
 * is the next bus" is answerable without routing anywhere.
 *
 * `searchAlso` is what makes them findable by the things people actually know:
 * the word "bus", the number printed on the pole, and the route they are
 * waiting for.
 *
 * Idempotent — a re-attach replaces the previous set rather than doubling it.
 *
 * @param {object} mapData
 * @param {object[]} stopPlaces  from transitStopPlaces()
 */
export function attachTransitStops(mapData, stopPlaces) {
  if (!mapData?.roomIndex) return [];
  mapData.roomIndex = mapData.roomIndex.filter(e => !e.busStop);
  const added = [];
  for (const p of stopPlaces || []) {
    const nodeId = p.routeNodeId;
    if (!nodeId || !mapData.nodes.get(nodeId)) continue;
    const t = p.transit || {};
    added.push({
      building: 'outdoors',
      // With no roomNumber, roomLabel() shows this alone — which is what the
      // sign on the pole says.
      buildingName: p.name,
      roomNumber: '',
      nodeId,
      nodeIds: [nodeId],
      busStop: true,
      placeId: p.id,
      // "Exiting UofM" / "Entering UofM" — see campusDirection(). Two stops
      // face each other across Dafoe with nearly the same name, and in a list
      // of search results the compass word is the only thing separating them.
      busNote: campusDirection(t.direction),
      searchAlso: ['bus', 'stop', 'transit', t.number ?? '', t.key ?? '', ...(t.routes || [])]
        .filter(Boolean).join(' '),
    });
  }
  mapData.roomIndex.push(...added);
  return added;
}

export function attachRoomInfo(mapData, rows) {
  const result = { matched: 0, total: mapData.roomIndex.length, unmatchedBuildings: [] };
  if (!Array.isArray(rows) || rows.length === 0) return result;

  const apps = mapData.buildings.map(b => ({ slug: b.slug, tokens: normNameTokens(b.name || b.slug) }));
  const resolveCache = new Map();
  const resolveSlug = (buildingName) => {
    if (resolveCache.has(buildingName)) return resolveCache.get(buildingName);
    const tk = normNameTokens(buildingName);
    let best = null, bestScore = 0;
    for (const a of apps) {
      const s = subsetMatchScore(tk, a.tokens);
      if (s > bestScore) { bestScore = s; best = a.slug; }
    }
    resolveCache.set(buildingName, best);
    return best;
  };

  const info = new Map();
  const unmatched = new Set();
  for (const r of rows) {
    const slug = resolveSlug(r.building);
    if (!slug) { unmatched.add(r.building); continue; }
    info.set(`${slug}::${String(r.roomNumber).toLowerCase()}`, r);
  }
  for (const e of mapData.roomIndex) {
    const hit = info.get(`${e.building}::${e.roomNumber.toLowerCase()}`);
    if (hit) {
      e.roomType = hit.roomType;
      e.roomTypeDetail = hit.roomTypeDetail;
      e.capacity = hit.capacity;
      result.matched++;
    }
  }
  mapData.roomInfo = info;
  result.unmatchedBuildings = [...unmatched];
  return result;
}

/**
 * Annotate mapData.roomIndex entries in place with a friendly `roomName` from
 * the per-building RoomNames sheets. `rows` is `[{building, roomNumber,
 * roomName}]` where `building` is already the app slug (the CSV filename), so
 * this is a direct join — no fuzzy building matching needed.
 */
export function attachRoomNames(mapData, rows) {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const names = new Map(); // `${slug}::${roomNumber}` → name
  for (const r of rows) {
    const name = String(r.roomName || '').trim();
    if (!name) continue;
    names.set(`${r.building}::${String(r.roomNumber).toLowerCase()}`, name);
  }
  for (const e of mapData.roomIndex) {
    const nm = names.get(`${e.building}::${e.roomNumber.toLowerCase()}`);
    if (nm) e.roomName = nm;
  }
}

/** Primary display label for a search entry. When a friendly room name exists
 *  it stands alone ("SSA Lounge"); otherwise it's "Building Number"
 *  ("Biological Sciences 100"), or just the building name when there's neither
 *  (e.g. "Parking Lot A"). */
export function roomLabel(entry) {
  if (!entry) return '';
  if (entry.roomName) return entry.roomName;
  return entry.roomNumber ? `${entry.buildingName} ${entry.roomNumber}` : entry.buildingName;
}

/** Muted secondary label shown to the right of a result — the "Building Number"
 *  ("Armes Lecture Theatre 209"), but only when a friendly name occupies the
 *  main label (otherwise the main label already carries this). */
export function roomNumberBadge(entry) {
  if (!entry || !entry.roomName) return '';
  return entry.roomNumber ? `${entry.buildingName} ${entry.roomNumber}` : entry.buildingName;
}

/** Average of every vertex across one or more rings — a good-enough centroid. */
function ringsCentroid(rings) {
  let lat = 0, lng = 0, n = 0;
  for (const ring of rings) for (const p of ring) { lat += p.lat; lng += p.lng; n++; }
  return n ? { lat: lat / n, lng: lng / n } : null;
}

/**
 * Resolves where a room search entry sits on the map: its floor, the polygon
 * ring(s) that make it up (for highlighting), and a centroid to fly to. Falls
 * back to the door node's own position when no matching polygon is found.
 * Returns { position, floor, rings, building } or null.
 */
export function getRoomLocation(mapData, entry) {
  if (!entry) return null;
  const node = mapData.nodes.get(entry.nodeId);
  const floor = node?.floor ?? 1;
  const b = mapData.buildings.find(x => x.slug === entry.building);
  let rings = [];
  if (b) {
    const target = entry.roomNumber.toLowerCase();
    rings = (b.polygonsByFloor.get(floor) || [])
      .filter(p => String(p.roomNumber).toLowerCase() === target)
      .map(p => p.ring);
  }
  const position = (rings.length && ringsCentroid(rings)) || (node ? { lat: node.lat, lng: node.lng } : null);
  return { position, floor, rings, building: entry.building };
}

/**
 * Best origin node for a whole building (no specific room): a floor-1/ground
 * exit if one exists, else the exit/node nearest the building's outline
 * centroid. Returns a node id or null.
 */
export function buildingOriginNodeId(mapData, slug) {
  const inBuilding = [...mapData.nodes.values()].filter(n => n.building === slug);
  if (inBuilding.length === 0) return null;
  const exits = inBuilding.filter(n => n.type === 'exit');
  const pool = exits.length ? exits : inBuilding;
  const b = mapData.buildings.find(x => x.slug === slug);
  const center = b && b.outlines.length ? ringsCentroid(b.outlines) : null;
  if (!center) return pool[0].id;
  let best = pool[0], bestD = Infinity;
  for (const n of pool) {
    const d = (n.lat - center.lat) ** 2 + (n.lng - center.lng) ** 2;
    if (d < bestD) { bestD = d; best = n; }
  }
  return best.id;
}

/** Buildings whose name matches every query token (for From autocomplete). */
export function searchBuildings(buildings, query, limit = 6) {
  const tokens = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  return buildings
    .filter(b => b.name && tokens.every(t => b.name.toLowerCase().includes(t)))
    .slice(0, limit);
}

/**
 * Search rooms across all buildings. Every whitespace-separated token must
 * match either the building name or the room number (case-insensitive
 * substring; room numbers rank prefix matches first). "201" lists every
 * building's 201; "machray 201" narrows to one building.
 */
export function searchRooms(roomIndex, query, limit = 12) {
  const tokens = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const scored = [];
  for (const entry of roomIndex) {
    // Shaft "rooms" (105st, 128el…) are stairwells/elevators, not destinations —
    // keep them out of the user-facing search list.
    if (shaftTypeOf(entry.roomNumber)) continue;
    const room = entry.roomNumber.toLowerCase();
    const bldg = entry.buildingName.toLowerCase();
    const name = (entry.roomName || '').toLowerCase();
    // Other words an entry answers to, when its own label does not contain
    // them. A bus stop named "Westbound Dafoe at U of M Station" is not found
    // by "bus", by its stop number, or by the route somebody is waiting for —
    // and those are the three things a person actually knows about it. Scored
    // below the building name so a real name match still wins.
    const also = (entry.searchAlso || '').toLowerCase();
    // An exact name this entry IS, as opposed to words it merely contains. A
    // parking lot's key is the case that needs it: "D" is a substring of half
    // the lot names on campus, so only an exact match can put D Lot above
    // Freedman Crescent Lot. Ranked above a room-number match because it is the
    // same kind of claim — this row is literally the thing you typed.
    const key = (entry.searchKey || '').toLowerCase();
    let score = 0, ok = true;
    for (const t of tokens) {
      if (key && key === t) score += 120;
      else if (room === t) score += 100;
      else if (room.startsWith(t)) score += 50;
      else if (room.includes(t)) score += 20;
      else if (name.includes(t)) score += 15;
      else if (bldg.includes(t)) score += 10;
      else if (also.includes(t)) score += 8;
      else { ok = false; break; }
    }
    if (ok) scored.push({ entry, score });
  }
  // Bus stops sort BELOW every room, whatever they scored.
  //
  // Not a score penalty but a separate rank, because the two are not competing
  // for the same reader. This box is "find a room", and a stop's name is a
  // street ("Southbound Chancellor Matheson at Dafoe") — so a stop can match a
  // building word as strongly as the building itself and push the room somebody
  // was typing off the top of the list. Ranked below, they are still all there
  // for the searches that are actually about them ("bus", a stop number, a route
  // number), where nothing else matches at all.
  scored.sort((a, b) =>
    (a.entry.busStop ? 1 : 0) - (b.entry.busStop ? 1 : 0) ||
    b.score - a.score ||
    a.entry.roomNumber.localeCompare(b.entry.roomNumber, undefined, { numeric: true })
  );
  return scored.slice(0, limit).map(s => s.entry);
}
